import { setActiveRequestsCount } from "redux-store/slices/globalLoaderSlice";
import { requestManager } from ".";
import store from "../../redux-store";

/**
 * =============================================================================
 * Global Loader Redux Sync
 * =============================================================================
 *
 * This module:
 * - Subscribes to RequestManager (single source of truth)
 * - Mirrors active request count into Redux
 * - Adds anti-flicker UX protection
 * - Handles race conditions safely
 * - Self-heals rare desynchronization edge cases
 *
 * Architecture Principle:
 * RequestManager = real request state
 * Redux = UI-friendly representation of that state
 *
 * IMPORTANT:
 * UI blocking logic should rely on RequestManager directly,
 * not Redux, because Redux updates are intentionally delayed.
 * =============================================================================
 */

/**
 * -----------------------------------------------------------------------------
 * Anti-Flicker Configuration
 * -----------------------------------------------------------------------------
 *
 * SHOW_DELAY:
 *   Wait before showing loader to prevent flashing for fast requests.
 *
 * MIN_VISIBLE_TIME:
 *   Once visible, loader remains visible for this duration
 *   to prevent rapid show/hide flicker.
 */
const SHOW_DELAY = 150;
const MIN_VISIBLE_TIME = 300;

/**
 * -----------------------------------------------------------------------------
 * Internal State Tracking
 * -----------------------------------------------------------------------------
 */

/**
 * Last observed request count.
 * Prevents unnecessary Redux dispatches.
 */
let lastCount: number = -1;

/**
 * Timers used for delayed show / delayed hide behavior.
 */
let showTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Timestamp when loader became visible.
 * Used to enforce minimum visible duration.
 */
let loaderVisibleSince: number | null = null;

console.log("Global loader Redux sync initialized");

/**
 * -----------------------------------------------------------------------------
 * Main Subscription
 * -----------------------------------------------------------------------------
 *
 * Runs synchronously whenever RequestManager updates its active request count.
 */
requestManager.subscribe((count: number) => {
  /**
   * Skip if nothing actually changed.
   * Prevents redundant processing.
   */
  if (count === lastCount) return;
  lastCount = count;

  /**
   * ============================================================================
   * CASE 1: One or more requests started (count > 0)
   * ============================================================================
   */
  if (count > 0) {
    /**
     * If a hide was scheduled but a new request started,
     * cancel the hide immediately.
     */
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }

    /**
     * If loader is not visible and no show is scheduled,
     * schedule a delayed show.
     *
     * This prevents loader flash for very fast requests.
     */
    if (loaderVisibleSince === null && !showTimer) {
      showTimer = setTimeout(() => {
        /**
         * SAFETY CHECK:
         * If all requests finished before delay expired,
         * do not show loader.
         *
         * Prevents race-condition flicker.
         */
        if (requestManager.getCount() === 0) {
          showTimer = null;
          return;
        }

        // Mark loader as visible
        loaderVisibleSince = Date.now();

        // Dispatch latest count (avoid stale value)
        store.dispatch(setActiveRequestsCount(requestManager.getCount()));

        showTimer = null;
      }, SHOW_DELAY);
    }

    return;
  }

  /**
   * ============================================================================
   * CASE 2: All requests finished (count === 0)
   * ============================================================================
   */
  if (count === 0) {
    /**
     * If loader was scheduled to show but request finished quickly,
     * cancel the pending show.
     *
     * Fast-request optimization.
     */
    if (showTimer) {
      clearTimeout(showTimer);
      showTimer = null;
      return;
    }

    /**
     * If loader is currently visible,
     * enforce minimum visible duration before hiding.
     */
    if (loaderVisibleSince !== null) {
      const elapsed = Date.now() - loaderVisibleSince;

      // Remaining time required to satisfy MIN_VISIBLE_TIME
      const remaining = Math.max(MIN_VISIBLE_TIME - elapsed, 0);

      hideTimer = setTimeout(() => {
        // Hide loader
        store.dispatch(setActiveRequestsCount(0));

        // Reset visibility tracking
        loaderVisibleSince = null;
        hideTimer = null;
      }, remaining);

      return;
    }

    /**
     * ULTRA-STABILITY RECONCILIATION GUARD
     *
     * If:
     * - No active requests
     * - Loader is not visible
     * - No timers pending
     *
     * Force Redux into correct idle state.
     *
     * Protects against:
     * - React StrictMode double-invocation
     * - Hot module reload (HMR)
     * - Rare missed dispatch edge cases
     */
    if (!loaderVisibleSince && !showTimer) {
      store.dispatch(setActiveRequestsCount(0));
    }
  }
});
